/**
 * useNotifications Hook
 * Convenient hook for managing notifications with type-specific helpers
 */

import { useCallback, useMemo } from 'react';
import { useNotifications as useNotificationsStore, useNotificationActions } from '@/store';
import type { Notification } from '@/store';

/**
 * Notification options (without id and timestamp)
 */
export type NotificationOptions = Omit<Notification, 'id' | 'timestamp' | 'read'>;

/**
 * Message-only notification options
 */
export type SimpleNotificationOptions = Omit<NotificationOptions, 'type'>;

/**
 * Notification hook return type
 */
interface UseNotificationsReturn {
  // Current notifications
  notifications: Notification[];

  // Generic notification
  notify: (options: NotificationOptions) => void;

  // Type-specific helpers
  success: (message: string, options?: Partial<SimpleNotificationOptions>) => void;
  error: (message: string, options?: Partial<SimpleNotificationOptions>) => void;
  warning: (message: string, options?: Partial<SimpleNotificationOptions>) => void;
  info: (message: string, options?: Partial<SimpleNotificationOptions>) => void;

  // Management
  markRead: (id: string) => void;
  remove: (id: string) => void;
  clear: () => void;
}

/**
 * useNotifications Hook
 * Provides notification management with convenient type-specific helpers
 */
export const useNotifications = (): UseNotificationsReturn => {
  const notifications = useNotificationsStore();
  const { addNotification, removeNotification, markNotificationRead, clearNotifications } =
    useNotificationActions();

  // Generic notification
  const notify = useCallback(
    (options: NotificationOptions) => {
      addNotification(options);
    },
    [addNotification]
  );

  // Success notification
  const success = useCallback(
    (message: string, options?: Partial<SimpleNotificationOptions>) => {
      addNotification({
        type: 'success',
        message,
        ...options,
      });
    },
    [addNotification]
  );

  // Error notification
  const error = useCallback(
    (message: string, options?: Partial<SimpleNotificationOptions>) => {
      addNotification({
        type: 'error',
        message,
        duration: 7000, // Errors stay longer
        ...options,
      });
    },
    [addNotification]
  );

  // Warning notification
  const warning = useCallback(
    (message: string, options?: Partial<SimpleNotificationOptions>) => {
      addNotification({
        type: 'warning',
        message,
        duration: 6000,
        ...options,
      });
    },
    [addNotification]
  );

  // Info notification
  const info = useCallback(
    (message: string, options?: Partial<SimpleNotificationOptions>) => {
      addNotification({
        type: 'info',
        message,
        ...options,
      });
    },
    [addNotification]
  );

  // Mark notification as read
  const markRead = useCallback(
    (id: string) => {
      markNotificationRead(id);
    },
    [markNotificationRead]
  );

  // Remove notification
  const remove = useCallback(
    (id: string) => {
      removeNotification(id);
    },
    [removeNotification]
  );

  // Clear all notifications
  const clear = useCallback(() => {
    clearNotifications();
  }, [clearNotifications]);

  return useMemo(
    () => ({
      notifications,
      notify,
      success,
      error,
      warning,
      info,
      markRead,
      remove,
      clear,
    }),
    [notifications, notify, success, error, warning, info, markRead, remove, clear]
  );
};

/**
 * Example usage:
 *
 * const { success, error, notifications } = useNotifications();
 *
 * // Simple success message
 * success('User created successfully');
 *
 * // Error with description
 * error('Failed to save', {
 *   description: 'Please check your connection and try again',
 *   duration: 10000, // 10 seconds
 * });
 *
 * // Custom notification
 * notify({
 *   type: 'warning',
 *   message: 'Session expiring soon',
 *   description: 'You will be logged out in 5 minutes',
 *   duration: undefined, // No auto-dismiss
 * });
 */
